// MCHD Room Scheduler - Room Management Component
// Manages rooms for a specific floor

import React, { useState } from 'react';
import { Paper, Title, Group, Button, TextInput, Select, Text, Stack, Alert, Badge, ActionIcon, Menu, Card, SimpleGrid, Box, Skeleton } from '@mantine/core';
import { IconPlus, IconSearch, IconRefresh, IconDots, IconEdit, IconTrash, IconHome, IconUsers, IconClock, IconEye } from '@tabler/icons-react';
import { useRooms, useCreateRoom, useUpdateRoom, useDeleteRoom } from '../../../hooks/coordinator/useBuildingManagement';
import { RoomForm } from './RoomForm';
import { RoomDetailSidebar } from '../../../components/drawers/RoomDetailSidebar';

interface Building {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
}

interface Floor {
  id: string;
  number: number;
  name: string;
  buildingId: string;
  isActive: boolean;
}

interface Room {
  id: string;
  number: string;
  name: string;
  floorId: string;
  capacity: number;
  description?: string | null;
  specialtyTags: string[];
  allowedSpecialties: string[];
  defaultBuffer: number;
  status: 'ACTIVE' | 'OUT_OF_SERVICE';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RoomManagementProps {
  floor: Floor;
  building: Building;
}

/**
 * Room Management Component
 * Manages rooms for a specific floor
 */
export const RoomManagement: React.FC<RoomManagementProps> = ({
  floor,
  building
}) => {
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sidebarRoomId, setSidebarRoomId] = useState<string | null>(null);

  const {
    data: rooms,
    isLoading: loading,
    error,
    refetch
  } = useRooms(floor.id);

  const createRoomMutation = useCreateRoom();
  const updateRoomMutation = useUpdateRoom();
  const deleteRoomMutation = useDeleteRoom();

  /**
   * Handle creating a new room
   */
  const handleCreateRoom = () => {
    setSelectedRoom(null);
    setShowForm(true);
  };

  /**
   * Handle editing an existing room
   */
  const handleEditRoom = (room: Room) => {
    setSelectedRoom(room);
    setShowForm(true);
  };

  /**
   * Handle room form submission
   */
  const handleFormSubmit = async (roomData: any) => {
    try {
      if (selectedRoom) {
        await updateRoomMutation.mutateAsync({ id: selectedRoom.id, data: roomData });
      } else {
        await createRoomMutation.mutateAsync({ ...roomData, floorId: floor.id });
      }
      setShowForm(false);
      setSelectedRoom(null);
    } catch (error) {
      console.error('Error saving room:', error);
    }
  };

  /**
   * Handle room deletion
   */
  const handleDeleteRoom = async (roomId: string) => {
    try {
      await deleteRoomMutation.mutateAsync(roomId);
    } catch (error) {
      console.error('Error deleting room:', error);
    }
  };

  /**
   * Handle room row click to open sidebar
   */
  const handleRoomClick = (room: Room) => {
    setSidebarRoomId(room.id);
  };

  /**
   * Handle closing sidebar
   */
  const handleCloseSidebar = () => {
    setSidebarRoomId(null);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  /**
   * Get status color for badge
   */
  const getStatusColor = (status: string, isActive: boolean) => {
    if (!isActive) return 'red';
    return status === 'ACTIVE' ? 'green' : 'orange';
  };

  /**
   * Get status text
   */
  const getStatusText = (status: string, isActive: boolean) => {
    if (!isActive) return 'Inactive';
    return status === 'ACTIVE' ? 'Active' : 'Out of Service';
  };

  return (
    <Stack gap="md">
      {/* Modern Header */}
      <Paper 
        p="lg" 
        withBorder 
        radius="lg"
        style={{ 
          background: 'white',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
          border: '1px solid #e2e8f0'
        }}
      >
        <Group justify="space-between" align="flex-start">
          <Group gap="lg">
            {/* Icon with MCHD Brand Colors */}
            <Box
              p="md"
              style={{
                background: 'linear-gradient(135deg, #28A8E0 0%, #1e8bc3 100%)',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(40, 168, 224, 0.15)'
              }}
            >
              <IconHome size={32} color="white" />
            </Box>
            
            {/* Title Section */}
            <div>
              <Title order={1} c="dark" fw={700} size="1.8rem" mb="xs">
                Floor {floor.number} - Rooms
              </Title>
              <Text c="dimmed" size="md" fw={400} style={{ lineHeight: 1.5 }}>
                {building.name} • {floor.name} • Manage room configurations and scheduling
              </Text>
              
              {/* Status Indicator */}
              <Group gap="xs" mt="sm">
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#10b981'
                  }}
                />
                <Text size="sm" c="dimmed" fw={500}>
                  {rooms?.length || 0} rooms configured
                </Text>
              </Group>
            </div>
          </Group>
          
          {/* Action Buttons */}
          <Group gap="md">
            <Button
              variant="light"
              leftSection={<IconRefresh size={18} />}
              onClick={() => refetch()}
              loading={loading}
              radius="md"
              size="md"
              style={{ 
                border: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                color: '#64748b',
                fontWeight: 500
              }}
            >
              Refresh
            </Button>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={handleCreateRoom}
              radius="md"
              size="md"
              style={{
                background: 'linear-gradient(135deg, #28A8E0 0%, #1e8bc3 100%)',
                border: 'none',
                fontWeight: 600,
                fontSize: '14px'
              }}
            >
              Add Room
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert color="red" title="Error">
          {error.message || 'An error occurred'}
        </Alert>
      )}

      {/* Modern Filters */}
      <Paper 
        p="lg" 
        withBorder 
        radius="lg"
        style={{ 
          background: 'white',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
          border: '1px solid #e2e8f0'
        }}
      >
        <Group gap="lg" align="end">
          <TextInput
            placeholder="Search rooms, specialties, room numbers..."
            leftSection={<IconSearch size={18} color="#64748b" />}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.currentTarget.value)}
            style={{ flex: 1 }}
            radius="md"
            size="md"
            styles={{
              input: {
                border: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                fontSize: '14px',
                height: '44px',
                '&:focus': {
                  borderColor: '#28A8E0',
                  backgroundColor: 'white',
                  boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)'
                },
                '&::placeholder': {
                  color: '#94a3b8'
                }
              }
            }}
          />
          <Select
            placeholder="All Status"
            data={[
              { value: '', label: 'All Status' },
              { value: 'ACTIVE', label: 'Active Only' },
              { value: 'OUT_OF_SERVICE', label: 'Out of Service' }
            ]}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value || '')}
            clearable
            radius="md"
            size="md"
            styles={{
              input: {
                border: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                fontSize: '14px',
                height: '44px',
                '&:focus': {
                  borderColor: '#28A8E0',
                  backgroundColor: 'white',
                  boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)'
                }
              }
            }}
          />
        </Group>
      </Paper>

      {/* Rooms Card Grid */}
      <Paper withBorder radius="md" p={0}>
        {loading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="md" p="lg">
            {Array.from({ length: 6 }).map((_, index) => (
              <Card key={index} shadow="sm" radius="md" p="lg">
                <Stack gap="md">
                  <Group gap="sm">
                    <Skeleton height={32} width={60} radius="md" />
                    <div style={{ flex: 1 }}>
                      <Skeleton height={18} width="80%" mb="xs" />
                      <Skeleton height={14} width="60%" />
                    </div>
                  </Group>
                  <Group gap="sm">
                    <Skeleton height={24} width={80} />
                    <Skeleton height={24} width={80} />
                  </Group>
                  <Group gap="xs">
                    <Skeleton height={20} width={60} />
                    <Skeleton height={20} width={60} />
                    <Skeleton height={20} width={60} />
                  </Group>
                  <Group justify="space-between">
                    <Skeleton height={24} width={80} />
                    <Skeleton height={32} width={32} circle />
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        ) : !Array.isArray(rooms) || rooms.length === 0 ? (
          <Stack align="center" gap="lg" py="xl">
            <IconHome size={64} color="var(--mantine-color-gray-4)" />
            <Text size="lg" fw={500} c="dimmed">
              No rooms on Floor {floor.number} yet
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Create rooms to start scheduling
            </Text>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleCreateRoom}
              style={{
                backgroundColor: '#28A8E0',
                border: 'none'
              }}
            >
              Add Room
            </Button>
          </Stack>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="md" p="lg">
            {(Array.isArray(rooms) ? rooms : []).map((room) => (
              <Card 
                key={room.id}
                shadow="sm" 
                radius="md" 
                p="lg"
                style={{
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  border: '1px solid transparent'
                }}
                onClick={() => handleRoomClick(room)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.borderColor = '#28A8E0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <Stack gap="md">
                  {/* Room Header */}
                  <Group gap="sm" align="flex-start">
                    <Badge
                      leftSection={<IconHome size={14} />}
                      color="blue"
                      variant="light"
                      size="lg"
                      style={{ fontWeight: 600 }}
                    >
                      {room.number}
                    </Badge>
                    <div style={{ flex: 1 }}>
                      <Text size="md" fw={600} mb="xs">
                        {room.name}
                      </Text>
                      {room.description && (
                        <Text size="sm" c="dimmed">
                          {room.description}
                        </Text>
                      )}
                    </div>
                  </Group>

                  {/* Capacity and Buffer */}
                  <Group gap="lg">
                    <Group gap="xs">
                      <IconUsers size={16} color="#28A8E0" />
                      <Text size="sm" fw={500}>
                        Capacity: {room.capacity}
                      </Text>
                    </Group>
                    <Group gap="xs">
                      <IconClock size={16} color="#28A8E0" />
                      <Text size="sm" fw={500}>
                        Buffer: {room.defaultBuffer}m
                      </Text>
                    </Group>
                  </Group>

                  {/* Specialties */}
                  <Box>
                    <Text size="sm" fw={500} mb="xs" c="dimmed">
                      Specialties:
                    </Text>
                    <Group gap="xs">
                      {room.specialtyTags.slice(0, 2).map((tag, index) => (
                        <Badge key={index} size="sm" variant="light" color="blue">
                          {tag}
                        </Badge>
                      ))}
                      {room.specialtyTags.length > 2 && (
                        <Text size="sm" c="dimmed">
                          +{room.specialtyTags.length - 2} more
                        </Text>
                      )}
                    </Group>
                  </Box>

                  {/* Status and Actions */}
                  <Group justify="space-between" align="center">
                    <Badge
                      color={getStatusColor(room.status, room.isActive)}
                      variant="light"
                      size="sm"
                    >
                      {getStatusText(room.status, room.isActive)}
                    </Badge>
                    
                    <Group gap="xs">
                      <Button
                        size="sm"
                        variant="subtle"
                        leftSection={<IconEye size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRoomClick(room);
                        }}
                        style={{
                          color: '#28A8E0'
                        }}
                      >
                        View Details
                      </Button>
                      
                      <Menu shadow="md" width={200}>
                        <Menu.Target>
                          <ActionIcon 
                            variant="subtle" 
                            c="gray"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditRoom(room);
                            }}
                          >
                            Edit Room
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            c="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRoom(room.id);
                            }}
                          >
                            Delete Room
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Group>

                  {/* Created Date */}
                  <Text size="xs" c="dimmed">
                    Created: {formatDate(room.createdAt)}
                  </Text>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Paper>

      {/* Room Form Modal */}
      {showForm && (
        <RoomForm
          room={selectedRoom}
          floorId={floor.id}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setShowForm(false);
            setSelectedRoom(null);
          }}
        />
      )}

      {/* Room Detail Sidebar */}
      <RoomDetailSidebar
        roomId={sidebarRoomId}
        opened={!!sidebarRoomId}
        onClose={handleCloseSidebar}
      />
    </Stack>
  );
};
